/* ----------------------------------------
    Node Modules
---------------------------------------- */

import anime from "animejs";

/* ----------------------------------------
    Utilites
---------------------------------------- */

import * as dom from '@util/dom';

/* ----------------------------------------
    Calculate REM
---------------------------------------- */

const calcRem = rem => {
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
};

/* ----------------------------------------
    Generate Random Number
---------------------------------------- */

const randomNumber = (start, end) => {
    return Math.floor(Math.random() * end) + start
};

/** ----------------------------------------
     Wave
 ---------------------------------------- */

const wave = item => {
    const randomPos = randomNumber(calcRem(2), calcRem(4));
    const randomDirection = Math.round(Math.random());

    return new Promise(resolve => {
        anime({
            targets: item,
            top: randomDirection ? [-Math.abs(randomPos), randomPos] : [randomPos, -Math.abs(randomPos)],
            easing: 'easeInOutQuad',
            duration: randomNumber(5000, 6000),
            loop: true,
            direction: 'alternate',
            update: () => {
                const left = parseInt(item.style.left, 10);
                const width = -Math.abs(item.clientWidth + window.innerWidth * 0.25);
                if (left < width) resolve(item);
            }
        });
    });
};

/* ----------------------------------------
    Gallery
---------------------------------------- */

export class GallerySlider {

    constructor(gallery, options) {
        this.gallery = gallery;
        this.items = [];
        this.options = options;

        this._amount = options.amount || 3;

        this._dom = {};
        this._sizes = {};

        this._init();
    }

    /** ----------------------------------------
         Max Width
     ---------------------------------------- */

    _maxWidth() {
        return this._sizes.item > this._max ? this._max : this._sizes.item;
    }

    /** ----------------------------------------
        Elements
     ---------------------------------------- */

    _setElements() {
        if(this.options.elements) {
            Object.keys(this.options.elements).forEach(option => {
                this._dom[option] = this.options.elements[option];
            });
        }
    }

    _duplicateElements() {
        const clones = [];

        const wrapper = document.querySelector(this._dom.holder);
        const items = wrapper.querySelectorAll(this._dom.item);

        [...items].forEach(item => {
            const clone = item.cloneNode(true);
            clones.push(clone);
        });

        [...clones].forEach(clone => {
            wrapper.insertAdjacentHTML('beforeend', clone.outerHTML);
        });
    }

    _setOptions() {
        Object.keys(this.options).forEach(option => {
            if(option !== 'elements') this['_' + option] = this.options[option];
        });
    }

    /** ----------------------------------------
        Set Items
     ---------------------------------------- */

    _setItems() {
        const wrapper = document.querySelector(this._dom.holder);
        const items = wrapper.querySelectorAll(this._dom.item);

        [...items].forEach(item => {
            item.style.width = dom.unit(this._sizes.item);
        });

        this._sizes.height = items[0].offsetHeight;

        this.items = [...items].map(item => item);
    }

    _setItemsPosition(speed) {
        [...this.items].forEach((item, index) => {
            const offset = index >= 1 && calcRem(this._gutter) * index || 0;
            item.style.left = dom.unit((this._maxWidth() * index) + offset - speed);
        });
    }

    _setItemPosition() {
        const last = [...this.items].slice(-1)[0];

        return {
            width: this._maxWidth(),
            left: parseInt(last.style.left, 10) + this._gutter + this._sizes.item
        };
    }

    /** ----------------------------------------
         Clone Elements
     ---------------------------------------- */

    _setWrapperSize() {
        const wrapper = this.gallery.querySelector(this._dom.holder);
        this._sizes.container = wrapper.offsetWidth;
        wrapper.style.height = dom.unit(this._sizes.height);
    }

    _setItemSize() {
        [...this.items].forEach(item => {
            item.style.width = dom.unit(this._maxWidth());
            wave(item).then(item => this._cloneItem(item));
        });

        this._sizes.height = this.items[0].offsetHeight;
    }

    _setSize() {
        this._sizes.item = Math.round(window.innerWidth / this._amount);
        this._sizes.window = window.innerWidth;

        this._setItemSize();
        this._setItemsPosition();

        this._setWrapperSize();
    }

    /** ----------------------------------------
         Animate Items
     ---------------------------------------- */

    _animateItems(speed) {
        this._setItemsPosition(speed)
    }

    /** ----------------------------------------
         Clone Item
     ---------------------------------------- */

    _selectClone() {
        const wrapper = this.gallery.querySelector(this._dom.holder);
        const items = wrapper.querySelectorAll(this._dom.item);
        return items[items.length- 1];
    }

    _cloneItem(item) {
        const wrapper = this.gallery.querySelector(this._dom.holder);
        const clone = item.cloneNode(true);

        const pos = this._setItemPosition(item);

        clone.style.width = dom.unit(pos.width);
        clone.style.left = pos.left;

        wrapper.insertAdjacentHTML('beforeend', clone.outerHTML);

        const added = this._selectClone();
        wave(added).then(item => this._cloneItem(item));

        item.remove();

        this.items.push(added);
    }

    /** ----------------------------------------
        Events
     ---------------------------------------- */

    _animate() {
        let start = null;

        const animate = timestamp => {
            if (!start) start = timestamp;
            const progress = timestamp - start;
            const speed = Math.round(progress * this._speed);

            this._animateItems(speed);

            window.requestAnimationFrame(animate);
        };

        window.requestAnimationFrame(animate);
    }

    _resize() {
        window.addEventListener('resize', () => this._setSize());
    }

    /** ----------------------------------------
        Initialize
     ---------------------------------------- */

    _init() {
        this._setElements();
        this._duplicateElements();
        this._setOptions();

        this._setItems();
        this._setSize();

        this._resize();
        this._animate();
    }

}
